import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, getFormValues } from 'redux-form';
import {
  MotifButton,
  MotifDropdown,
  MotifDropdownItem,
  MotifIcon,
  MotifTooltip,
} from '@ey-xd/motif-react';
import { addAttribute } from '../../common/Attribute';
import * as formHelpers from '../../scripts/formHelpers';
import { IconoirNavArrowDown, IconoirInfoCircle } from '@ey-xd/motif-icon';
import { connect } from 'react-redux';

const OwnershipHeader = ({
  reportPeriods,
  handleReportPeriodSelection,
  disableAction,
  handleExportOwnershipImportTemplate,
  handleUploadOwnershipImportTemplate,
  isReportPeriodSpecific,
  handleEditOwnershipPercentage,
  isEditModeActive,
  defaultReportPeriod,
}) => {
  const [actionOptionsOpen, setActionOptionsOpen] = useState(false);

  // Combine yearOptions with quarterOptionValues
  const combinedOptions = [];
  reportPeriods?.forEach((periodDescription) => {
    // Period Description is in format like "Q1 2023"
    const [quarter, year] = periodDescription.split(' ');
    combinedOptions.push({
      key: `${quarter} ${year}`,
      text: `${quarter} ${year}`,
      value: `${quarter} ${year}`,
    });
  });

  useEffect(() => {
    addAttribute("button[role = 'combobox']", 'type', 'button');
  });

  return (
    <>
      <div style={{ minHeight: '24px' }}></div>
      {/* If no data for report period, show message and skip header form */}
      <form>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            className="mr-3 mt-auto mr-2"
            style={{
              flex: '0 0 auto',
              marginBottom: '50px',
              minWidth: '200px',
            }}
          >
            {!isReportPeriodSpecific && (
              <Field
                name="reportPeriod"
                label="Report Period"
                type="select"
                testingData={{
                  label: 'lblReportPeriod',
                  div: 'divReportPeriod',
                }}
                onChange={handleReportPeriodSelection}
                options={combinedOptions}
                component={formHelpers.SingleSelectComponent}
                defaultValue={defaultReportPeriod ? defaultReportPeriod : ''}
                disabled={isEditModeActive}
                hideClearButton={true}
              />
            )}
          </div>
          <div
            className="mt-auto"
            style={{ flex: '0 0 auto', marginBottom: '11px' }}
          >
            {!isReportPeriodSpecific && isEditModeActive && (
              <MotifTooltip
                placement="right"
                trigger={
                  <MotifIcon
                    iconFunction={IconoirInfoCircle}
                    size="24"
                    data-testid="InfoReportPeriod"
                    aria-label="Report Period Info"
                  />
                }
              >
                <div>Exit Edit mode to change the Report Period.</div>
              </MotifTooltip>
            )}
          </div>
          <div style={{ flex: '1' }}></div>
          {/* Hide actions dropdown if noDataForReportPeriod is true */}
          <div style={{ width: 'fit-content', marginBottom: '10px' }}>
            <MotifDropdown
              open={actionOptionsOpen}
              placement="bottom-end"
              id={'action-dropdown'}
              className="master-data-dropdown-action"
              handleClickOutside={() => setActionOptionsOpen(false)}
              trigger={
                <MotifButton
                  type="button"
                  variant="primary"
                  onClick={() => setActionOptionsOpen(!actionOptionsOpen)}
                  aria-controls={'action-dropdown'}
                  id={'action-dropdown-trigger'}
                  aria-expanded={actionOptionsOpen}
                  aria-label="Action dropdown"
                  data-testid="btnActions"
                  disabled={!disableAction}
                >
                  Actions{' '}
                  <MotifIcon
                    iconFunction={IconoirNavArrowDown}
                    size="24"
                    fill="none"
                  />
                </MotifButton>
              }
            >
              <ul role="menu">
                <li role="menuitem">
                  <MotifDropdownItem
                    type="button"
                    onClick={handleExportOwnershipImportTemplate}
                    data-testid="btnExportPropertiesOwnership%"
                    className="master-dropdown-item"
                  >
                    Export Properties Ownership %
                  </MotifDropdownItem>
                </li>
                <li role="menuitem">
                  <MotifDropdownItem
                    type="button"
                    onClick={handleUploadOwnershipImportTemplate}
                    data-testid="btnUploadPropertiesOwnership%"
                    className="master-dropdown-item"
                  >
                    Upload Properties Ownership %
                  </MotifDropdownItem>
                </li>
                <li role="menuitem">
                  <MotifDropdownItem
                    type="button"
                    disabled={isEditModeActive}
                    onClick={handleEditOwnershipPercentage}
                    data-testid="btnEditPropertiesOwnership%"
                    className="master-dropdown-item"
                  >
                    Edit Properties Ownership %
                  </MotifDropdownItem>
                </li>
              </ul>
            </MotifDropdown>
          </div>
        </div>
      </form>
    </>
  );
};

OwnershipHeader.propTypes = {
  handleSubmit: PropTypes.func,
  clientId: PropTypes.number,
  handleReportPeriodSelection: PropTypes.func,
  latestReportPeriod: PropTypes.number, // Add prop type for defaultReportPeriod
  reportPeriods: PropTypes.array,
  disableAction: PropTypes.bool,
  handleExportOwnershipImportTemplate: PropTypes.func.isRequired,
  handleUploadOwnershipImportTemplate: PropTypes.func.isRequired,
  isReportPeriodSpecific: PropTypes.bool,
  isEditModeActive: PropTypes.bool,
  handleEditOwnershipPercentage: PropTypes.func.isRequired,
  defaultReportPeriod: PropTypes.string,
  noDataForReportPeriod: PropTypes.bool,
};

export default connect((state) => ({
  ownershipFormValues: getFormValues('ownershipForm')(state),
}))(reduxForm({ form: 'ownershipForm' })(OwnershipHeader));
